// Utility to build SLS dispatches for Essential Information Card edits.
// This works off of the shallow form value shape produced by prepareEssentialInformationDefaultValues.
// It compares original snapshot (pre-edit) to current snapshot (post-edit) and emits dispatches
// using existing SLS action types so the standard reducer & save pipeline can process them.

import {
  createFieldValue,
  batchCreateBasicField,
  batchCreateNameSuper,
  batchCreateName,
  batchCreateRecord,
  fieldToElementTypeMap,
  getNestedOrFloatingElementById,
  batchDeleteElementAndSubs,
  FEMALE,
  INTERSEX,
  MALE,
  UNKNOWN,
} from '@fs/zion-sls'
import * as yup from 'yup'
import { personDetailTypeKeys as personDetailTypes } from '../../shared/utils/helpers'
import { compareLinkedPersons, getSharedElement } from '../../panels/linkNamesPanel/utils/linkedPersonsHelpers'
import {
  linkSharedFieldToPerson,
  unlinkSharedFieldFromPerson,
} from '../../panels/linkNamesPanel/utils/namePartLinkingHelpers'
import { MAKE_NEW_RECORD } from '../../familyMembersCard/utils/saveRelationships'
import {
  createLinkPersonDispatches,
  createUnlinkPersonDispatches,
} from '../../panels/linkNamesPanel/utils/linkingDispatchHelpers'

// ---------- Yup Validation Schema ----------
// Derive valid part types dynamically from fieldToElementTypeMap (keys beginning with NAME_)
const PART_TYPES = Object.keys(fieldToElementTypeMap).filter((k) => k.startsWith('NAME_'))

// Part schema (validated indirectly via name schema test because parts are dynamic keys)
const partSchema = yup
  .object({
    elementId: yup.string().required('elementId required for name parts'), // ID of the intermediate element (e.g., NAME_GIVEN)
    id: yup.string().required('id required for name parts'), // ID of the field element
    added: yup.boolean().default(false),
    removed: yup.boolean().default(false), // allowed with added: true (removed takes precedence)
    type: yup.string().oneOf(PART_TYPES, 'invalid part type').notRequired(),
    value: yup.mixed().nullable(),
  })
  .noUnknown(true, 'unknown keys in part object')

// Name schema
const nameSchema = yup
  .object({
    id: yup.string().when(['added', 'removed'], {
      is: (added, removed) => added || !removed, // ID required for new names or existing non-removed names
      then: (schema) => schema.required('name.id required'),
      otherwise: (schema) => schema.optional(),
    }),
    added: yup.boolean().default(false),
    removed: yup.boolean().default(false), // allowed with added: true (removed takes precedence)
    type: yup
      .object({
        key: yup.string().nullable().optional(), // Made optional to support empty forms
        primaryText: yup.string().nullable(),
      })
      .nullable()
      .optional(),
    parts: yup
      .array()
      .of(yup.string().oneOf(PART_TYPES, 'invalid part type'))
      .default([]) // Default to empty array instead of required
      .test('unique-parts', 'duplicate part types', (arr) => !arr || new Set(arr).size === arr.length),
    removedParts: yup
      .array()
      .of(yup.string().oneOf(PART_TYPES, 'invalid part type'))
      .default([]) // Default to empty array
      .optional(),
  })
  .test(
    'parts-have-objects-or-removed',
    'each part must have object or be marked as removed',
    function partsHaveObjects(name) {
      if (!name?.parts) return true
      return name.parts.every((pt) => !!name[pt] || name[pt]?.removed === true)
    }
  )
  .test('part-objects-valid', 'invalid part object', function partObjectsValid(name) {
    if (!name?.parts) return true
    try {
      name.parts.forEach((pt) => {
        if (name[pt]) partSchema.validateSync(name[pt], { abortEarly: false })
      })
      return true
    } catch (e) {
      return this.createError({ message: e.errors?.join('; ') || 'invalid part object' })
    }
  })
  .noUnknown(false) // allow dynamic part keys

// Simple field schema reused for race & age (sex validated separately for its specialized object value)
const simpleFieldBaseSchema = yup
  .object({
    id: yup.string().when(['added', 'value'], {
      is: (added, value) => added || value != null, // ID required when adding or when value exists
      then: (schema) => schema.required('field.id required when value present'),
      otherwise: (schema) => schema.optional(),
    }),
    added: yup.boolean().default(false),
    value: yup.mixed().nullable(),
  })
  .noUnknown(true)

const allowedSexKeys = [UNKNOWN, MALE, FEMALE, INTERSEX]
  .filter(Boolean)
  .map((key) => (typeof key === 'string' ? key.toLowerCase() : key))

const standardSexKeys = new Set(allowedSexKeys)

const normalizeSimpleFieldValue = (value) => {
  if (value == null || value === '') return null
  if (typeof value === 'string') {
    const trimmed = value.trim()
    return trimmed.length ? trimmed : null
  }

  if (typeof value === 'object') {
    if (value.key != null || value.primaryText != null) {
      return value
    }
    return null
  }

  return null
}

const hasSimpleFieldSelection = (value) => Boolean(normalizeSimpleFieldValue(value))

// Root schema (current values) - supports both editing existing data and empty forms
const currentValuesSchema = yup
  .object({
    id: yup.string().required('person id required'),
    primaryPerson: yup.boolean().required(),
    recordId: yup.string().nullable().optional(),
    names: yup.array().of(nameSchema).default([]).min(0, 'names array cannot be negative'),
    removedNames: yup.array().of(nameSchema).default([]).min(0, 'removedNames array cannot be negative'),
    SEX_CODE: simpleFieldBaseSchema
      .shape({
        value: yup
          .mixed()
          .test('sex-object-valid', 'SEX_CODE.value.key must be male, female, unknown, or intersex', (val) => {
            if (val == null || val === '') return true
            if (typeof val !== 'object') return false
            const key = typeof val.key === 'string' ? val.key.trim().toLowerCase() : null
            if (!key) return true // Allow empty key for incomplete forms
            return standardSexKeys.has(key)
          })
          .nullable(),
      })
      .optional(),
  })
  .required()
  .noUnknown(true, 'unknown top-level keys in currentValues')

// Original values schema (slightly looser: names may be absent / empty)
const originalValuesSchema = currentValuesSchema
  .shape({
    primaryPerson: yup.boolean().nullable(),
    names: yup.array().of(nameSchema).default([]),
    removedNames: yup.array().of(nameSchema).default([]),
  })
  .noUnknown(true, 'unknown top-level keys in originalValues')

// Attribution helper (mirrors patterns elsewhere – empty reason/comment for simple field edits)
const buildAttribution = (appName) => ({ comment: '', reasonId: '', app: appName })

// Helper to check if a part value is meaningful (non-empty, non-null, non-whitespace-only)
const hasMeaningfulValue = (value) => {
  if (value == null) return false
  if (typeof value === 'string') return value.trim() !== ''
  return value != null
}

// Helper to check if a name has at least one meaningful part
const nameHasMeaningfulParts = (name) => {
  if (!name?.parts) return false
  return name.parts.some((partType) => {
    const part = name[partType]
    return part && !part.removed && hasMeaningfulValue(part?.value)
  })
}

// Determine if at least one active (non-removed) name with at least one active part with meaningful value exists
export const hasMinimumNameData = (values) => {
  if (!values?.names?.length) return false
  return values.names.some(
    (name) =>
      !name.removed &&
      name.parts?.some((partType) => {
        const part = name[partType]
        return (
          part &&
          !part?.removed &&
          part?.value &&
          (typeof part.value === 'string' ? part.value.trim() !== '' : part.value != null)
        )
      })
  )
}

// Build map of names by id for original snapshot to detect changes quickly
const indexOriginal = (original) => {
  const nameMap = {}
  original.names?.forEach((n) => {
    nameMap[n.id] = n
  })
  return { nameMap }
}

// Compare primitive-ish values (strings or object with key/value semantics)
const valueChanged = (before, after) => {
  if (before === after) return false
  if (typeof before === 'object' && typeof after === 'object') {
    if (!before && after) return true
    if (!after && before) return true
    return JSON.stringify(before) !== JSON.stringify(after)
  }
  return true
}

// Determine if the form is in a completely empty state (new record)
const isEmptyFormState = (values) => {
  if (!values) return true
  const hasNames = values.names?.some(
    (name) =>
      !name.removed &&
      name.parts?.some((partType) => {
        const part = name[partType]
        return part && !part?.removed && part?.value && part.value.trim() !== ''
      })
  )
  return !hasNames
}

// Enhanced validation that supports different modes for editing vs new forms
export const validateFormData = ({ originalValues, currentValues, partitionKey, personId, strict = true }) => {
  const isEmpty = isEmptyFormState(currentValues)

  // For empty forms, use more lenient validation
  if (isEmpty && !strict) {
    // Basic structural validation only
    try {
      currentValuesSchema.validateSync(currentValues, { abortEarly: false })
    } catch (e) {
      const message = Array.isArray(e.errors) ? e.errors.join(' | ') : e.message
      throw new Error(`[EI Dispatch Builder] basic validation failed: ${message}`)
    }
    return { isEmpty: true }
  }

  // For forms with data or strict mode, use full validation
  try {
    originalValuesSchema.validateSync(originalValues, { abortEarly: false })
    currentValuesSchema.validateSync(currentValues, { abortEarly: false })
  } catch (e) {
    const message = Array.isArray(e.errors) ? e.errors.join(' | ') : e.message
    throw new Error(`[EI Dispatch Builder] validation failed: ${message}`)
  }

  if (!partitionKey || typeof partitionKey !== 'string') {
    throw new Error('[EI Dispatch Builder] partitionKey must be non-empty string')
  }
  if (!personId || typeof personId !== 'string') {
    throw new Error('[EI Dispatch Builder] personId must be non-empty string')
  }

  // Duplicate id detection (only for forms with data)
  const idSet = new Set()
  currentValues.names?.forEach((n) => {
    if (n?.id && idSet.has(n.id)) throw new Error(`[EI Dispatch Builder] duplicate name id '${n.id}'`)
    if (n?.id) idSet.add(n.id)
    n.parts?.forEach((pt) => {
      const part = n[pt]
      if (part?.id) {
        if (idSet.has(part.id)) {
          throw new Error(`[EI Dispatch Builder] duplicate part id '${part.id}'`)
        }
        idSet.add(part.id)
      }
    })
  })

  return { isEmpty: false }
}

/**
 * Handle adding a new person detail field (SEX_CODE, RACE_OR_COLOR, AGE)
 * Creates appropriate dispatches based on field type
 *
 * @param {Object} options
 * @param {string} options.key - Field type key ('SEX_CODE', 'RACE_OR_COLOR', 'AGE')
 * @param {Object} options.cur - Current field data from form
 * @param {Object} options.current - Current person data
 * @param {string} options.partitionKey - Partition key for dispatches
 * @param {Object} options.attr - Attribution object
 * @param {Array} options.dispatches - Dispatch array to push to
 */
const handleAddNewField = ({ key, cur, current, partitionKey, attr, dispatches }) => {
  const fieldType = key // Use the key directly as it matches the field type
  let fv = {}

  if (key === 'SEX_CODE' && cur.value) {
    fv = createFieldValue(
      cur.value?.primaryText || '',
      attr,
      cur.value?.key ? `http://gedcomx.org/${cur.value.key}` : null
    )
    dispatches.push(
      ...batchCreateBasicField({
        partitionKey,
        superId: current.id, // person id
        id: cur.id,
        fieldType,
        fieldValue: fv,
        superElementType: 'PERSON',
      })
    )
  }
}

/**
 * Handle updating an existing person detail field value
 * Creates ADD_FV dispatch with appropriate field value formatting
 *
 * @param {Object} options
 * @param {string} options.key - Field type key ('SEX_CODE', 'RACE_OR_COLOR', 'AGE')
 * @param {Object} options.cur - Current field data from form
 * @param {string} options.partitionKey - Partition key for dispatches
 * @param {Object} options.attr - Attribution object
 * @param {Array} options.dispatches - Dispatch array to push to
 */
const handleUpdateFieldValue = ({ key, cur, partitionKey, attr, dispatches }) => {
  let fv
  if (key === 'SEX_CODE') {
    fv = createFieldValue(
      cur.value?.primaryText || '',
      attr,
      cur.value?.key ? `http://gedcomx.org/${cur.value.key}` : null
    )
  } else {
    fv = createFieldValue(cur.value || '', attr)
  }
  dispatches.push({ type: 'ADD_FV', value: { partitionKey, id: cur.id, fieldValue: fv } })
}

/**
 * Build dispatches for person detail fields (SEX_CODE, RACE_OR_COLOR, AGE)
 * Handles field creation, updates, removal, and link/unlink operations
 *
 * @param {Object} options
 * @param {Object} options.original - Original field values from form defaults
 * @param {Object} options.current - Current field values from form
 * @param {string} options.partitionKey - Partition key for dispatches
 * @param {string} options.appName - Application name for attribution
 * @param {Array} options.dispatches - Dispatch array to push to
 * @param {Array} options.imagesState - SLS images state
 */
const buildPersonDetailFieldDispatches = ({ original, current, partitionKey, appName, dispatches, imagesState }) => {
  const attr = buildAttribution(appName)
  personDetailTypes.forEach((key) => {
    const orig = original[key]
    const cur = current[key]
    if (!cur?.id) return

    if (
      key === 'SEX_CODE' &&
      orig?.id &&
      valueChanged(orig?.value, cur?.value) &&
      !hasSimpleFieldSelection(cur?.value)
    ) {
      const existingField = imagesState ? getNestedOrFloatingElementById(imagesState, orig.id) : null
      if (existingField) {
        const removalActions = batchDeleteElementAndSubs({ element: existingField }) || []

        if (!removalActions.length) {
          removalActions.push({
            type: 'REMOVE_ELEMENT',
            value: {
              id: existingField.id,
              partitionKey: existingField.partitionKey || partitionKey,
              elementType: existingField.elementType || 'FIELD',
            },
          })
        }

        dispatches.push(...removalActions)
      }
      return
    }

    const { addedPersons, removedPersons } = compareLinkedPersons(
      orig?.linkedPersonIds || [],
      cur.linkedPersonIds || []
    )

    // Added new field
    if (cur.added && cur.value && !orig?.value) {
      handleAddNewField({ key, cur, current, partitionKey, attr, dispatches })
    } else if (orig?.value && cur?.value && valueChanged(orig.value, cur.value)) {
      // Value update -> ADD_FV
      handleUpdateFieldValue({ key, cur, partitionKey, attr, dispatches })
    }

    // Linked person changes
    if (addedPersons.length || removedPersons.length) {
      addedPersons.forEach((p) => {
        const person = getNestedOrFloatingElementById(imagesState, p.id)

        if (!person) return

        const sharedElement = getSharedElement(imagesState, cur.id)

        const linkDispatches = createLinkPersonDispatches({
          person,
          sharedElement,
          images: imagesState,
        })

        dispatches.push(...linkDispatches)
      })

      removedPersons.forEach((p) => {
        const person = getNestedOrFloatingElementById(imagesState, p.id)

        if (!person) return

        const unlinkDispatches = createUnlinkPersonDispatches({
          person,
          fieldId: cur.id,
          fieldKey: key,
          partitionKey,
          images: imagesState,
        })

        dispatches.push(...unlinkDispatches)
      })
    }
  })
}
/**
 * Finds and returns the first dispatch object from the array that matches the specified element type
 * and has a type of 'CREATE_ELEMENT'.
 *
 * @param {Array<Object>} dispatches - The array of dispatch objects to search through.
 * @param {string} elementType - The element type to match within the dispatch objects.
 * @param {string} [elementId=null] - Optional element ID to match. If provided, only returns dispatch with matching ID.
 * @returns {Object|undefined} The matching dispatch object if found, otherwise undefined.
 */
const getCreateDispatchByElementType = (dispatches, elementType, elementId = null) => {
  return dispatches.find(
    (d) =>
      d.type === 'CREATE_ELEMENT' &&
      d.value.elementType === elementType &&
      (elementId ? d.value.id === elementId : true)
  )
}

/**
 * Checks if a dispatch for creating an element with the given ID already exists.
 *
 * @param {Array<Object>} dispatches - The array of dispatch objects to check.
 * @param {string} elementId - The element ID to look for.
 * @returns {boolean} True if a CREATE_ELEMENT dispatch exists for this ID.
 */
const isDispatchAlreadyCreated = (dispatches, elementId) => {
  return dispatches.some((d) => d.type === 'CREATE_ELEMENT' && d.value.id === elementId)
}

/**
 * Finds an existing element ID that can be reused for a given element type.
 * Checks both existing name parts and pending dispatches.
 *
 * @param {Object} name - The name object containing parts.
 * @param {string} elementType - The element type to find.
 * @param {Array<Object>} dispatches - Main dispatches array to check.
 * @param {Array<Object>} existingDispatches - Additional dispatches to check (e.g., from highlights).
 * @param {string} [targetElementId=null] - Optional target element ID to filter by. Prevents cross-NAME reuse.
 * @returns {string|undefined} The element ID if found, otherwise undefined.
 */
const findExistingElementId = ({ name, elementType, dispatches, existingDispatches = [], targetElementId = null }) => {
  // Check existing parts with same element type
  const existingPart = name.parts.find((pt) => {
    const nameField = name[pt]
    return !nameField.added && !nameField.removed && elementType === fieldToElementTypeMap[nameField.type]
  })

  if (existingPart) return name[existingPart]?.elementId

  // Check pending dispatches - filter by targetElementId to prevent cross-NAME reuse
  return getCreateDispatchByElementType([...dispatches, ...existingDispatches], elementType, targetElementId)?.value?.id
}

/**
 * Creates dispatches for intermediate name element (e.g., NAME_SURNAME) and links it to the NAME element.
 *
 * @param {string} nameId - The ID of the NAME element.
 * @param {string} elementId - The ID of the intermediate element to create.
 * @param {string} partitionKey - The partition key.
 * @param {string} elementType - The element type (e.g., NAME_SURNAME).
 */
const createIntermediateElementDispatches = ({ nameId, elementId, partitionKey, elementType }) => {
  const elementDispatches = [
    {
      type: 'CREATE_ELEMENT',
      value: { id: elementId, partitionKey, elementType },
    },
    {
      type: 'ADD_SUB',
      value: {
        superPartitionKey: partitionKey,
        subPartitionKey: partitionKey,
        superId: nameId,
        subId: elementId,
        elementType: 'NAME',
      },
    },
  ]
  return elementDispatches
}

/**
 * Processes existing field dispatches (e.g., from highlights) and adds them to the main dispatch queue.
 *
 * @param {Array<Object>} existingDispatches - Dispatches to process.
 * @param {Function} addDispatch - Function to add dispatches.
 */
const processExistingFieldDispatches = (existingDispatches, addDispatch) => {
  existingDispatches.forEach(addDispatch)
}

/**
 * Creates a new NAME element and all its parts (e.g., given name, surname).
 * Handles deduplication via addDispatch and reuses intermediate elements when possible.
 *
 * @param {Object} name - The name object with parts and metadata.
 * @param {string} partitionKey - The partition key.
 * @param {string} personId - The ID of the person this name belongs to.
 * @param {string} appName - Application name for attribution.
 * @param {Array<Object>} dispatches - Existing dispatches to check for reusable elements.
 * @param {Function} addDispatch - Function to add dispatches with deduplication.
 */
const createNewName = ({ name, partitionKey, personId, appName, dispatches = [], addDispatch }) => {
  const attr = buildAttribution(appName)
  batchCreateNameSuper({
    partitionKey,
    nameElementId: name.id,
    nameType: name?.type?.key,
    personId,
  }).forEach(addDispatch)

  // Create structure for each active part with meaningful data
  name.parts
    .filter((p) => name[p] && !name[p]?.removed && hasMeaningfulValue(name[p]?.value))
    .forEach((partType) => {
      const part = name[partType]
      const shouldCreatePart = part.added && !part.removed
      const elementType = fieldToElementTypeMap[partType]
      const fv = createFieldValue(part.value || '', attr)

      // Check what already exists for this field from highlight-provided dispatches
      const highlightDispatches = part.dispatches || []
      const fieldExistsInHighlights = isDispatchAlreadyCreated(highlightDispatches, part.id)
      const intermediateExistsInHighlights = isDispatchAlreadyCreated(highlightDispatches, part.elementId)

      // Check if another part in this batch already created the shared intermediate element
      const intermediateCreatedInBatch = getCreateDispatchByElementType(dispatches, elementType, part.elementId)
      const intermediateElementId = intermediateCreatedInBatch?.value?.id || part.elementId

      // Determine what needs to be created based on what exists
      const needsFieldCreation = shouldCreatePart && !fieldExistsInHighlights
      const needsIntermediateCreation = !intermediateExistsInHighlights && !intermediateCreatedInBatch

      if (needsFieldCreation && needsIntermediateCreation) {
        // Create complete structure: intermediate element + field + linkage (ADD_SUB)
        const newNameDispatches = batchCreateName({
          partitionKey,
          nameSuperId: name.id,
          fieldValue: fv,
          fieldType: partType,
          elementType,
          nameFieldId: part.id,
          nameElementId: intermediateElementId,
        })
        newNameDispatches.forEach(addDispatch)
        processExistingFieldDispatches(highlightDispatches, addDispatch)

        return
      }

      if (needsFieldCreation && !needsIntermediateCreation) {
        // Intermediate exists (created by another part), attach new field to it
        batchCreateBasicField({
          partitionKey,
          superId: intermediateElementId,
          superElementType: elementType,
          id: part.id,
          fieldType: partType,
          fieldValue: fv,
        }).forEach(addDispatch)

        processExistingFieldDispatches(highlightDispatches, addDispatch)

        return
      }

      if (needsIntermediateCreation) {
        // Intermediate element doesn't exist - create it and link field to it
        createIntermediateElementDispatches({
          nameId: name.id,
          elementId: intermediateElementId,
          partitionKey,
          elementType,
        }).forEach(addDispatch)

        processExistingFieldDispatches(highlightDispatches, addDispatch)

        // Link field to intermediate element
        addDispatch({
          type: 'ADD_SUB',
          value: {
            superPartitionKey: partitionKey,
            subPartitionKey: partitionKey,
            superId: intermediateElementId,
            subId: part.id,
            elementType,
          },
        })
      } else if (fieldExistsInHighlights) {
        // Both field and intermediate exist - just process highlight dispatches
        processExistingFieldDispatches(highlightDispatches, addDispatch)
      }
    })
}

/**
 * Adds a single new name part (e.g., given name, surname) to an existing NAME element.
 * Attempts to reuse existing intermediate elements to avoid duplicates.
 *
 * @param {Object} name - The name object containing all parts.
 * @param {Object} part - The specific part to add.
 * @param {string} partType - The type of part (e.g., NAME_GIVEN, NAME_SURNAME).
 * @param {string} partitionKey - The partition key.
 * @param {string} appName - Application name for attribution.
 * @param {Array<Object>} dispatches - Existing dispatches to check for reusable elements.
 * @param {Function} addDispatch - Function to add dispatches with deduplication.
 */
const createNewNamePart = ({ name, part, partType, partitionKey, appName, dispatches, addDispatch }) => {
  // Skip creating part if value is not meaningful
  if (!hasMeaningfulValue(part?.value)) {
    return
  }

  const highlightDispatches = part.dispatches || []
  const elementType = fieldToElementTypeMap[partType]

  // Check if an intermediate element already exists (from other existing parts or dispatches) and get its ID to reuse
  const intermediateElementId = findExistingElementId({
    name,
    elementType,
    dispatches,
    existingDispatches: highlightDispatches,
    targetElementId: part.elementId, // Prevent cross-NAME reuse by filtering to specific elementId
  })

  const attr = buildAttribution(appName)
  const fv = createFieldValue(part.value || '', attr)

  const fieldExistsInHighlights = isDispatchAlreadyCreated(highlightDispatches, part.id)
  const hasIntermediateElement = !!intermediateElementId
  const hasHighlightDispatches = highlightDispatches.length > 0

  if (hasIntermediateElement && hasHighlightDispatches) {
    // Intermediate exists and field is created in highlight dispatches - just process them
    processExistingFieldDispatches(highlightDispatches, addDispatch)
    return
  }

  if (hasIntermediateElement && !fieldExistsInHighlights) {
    // If just the intermediate exists, create the new field and link it to the intermediate element
    batchCreateBasicField({
      partitionKey,
      superId: intermediateElementId,
      superElementType: elementType,
      id: part.id,
      fieldType: partType,
      fieldValue: fv,
    }).forEach(addDispatch)

    // Then process any existing highlight dispatches for this part (if they exist)
    processExistingFieldDispatches(highlightDispatches, addDispatch)
    return
  }

  if (!hasIntermediateElement && !fieldExistsInHighlights) {
    // Create complete structure: intermediate + field + linkage (ADD_SUB)
    batchCreateName({
      partitionKey,
      nameSuperId: name.id,
      fieldValue: fv,
      fieldType: partType,
      elementType,
      nameFieldId: part.id,
      nameElementId: part.elementId,
    }).forEach(addDispatch)

    processExistingFieldDispatches(highlightDispatches, addDispatch)
    return
  }

  if (!hasIntermediateElement && fieldExistsInHighlights) {
    // Field exists from highlights but intermediate doesn't - create intermediate and link field
    createIntermediateElementDispatches({
      nameId: name.id,
      elementId: part.elementId,
      partitionKey,
      elementType,
    }).forEach(addDispatch)

    processExistingFieldDispatches(highlightDispatches, addDispatch)

    // Link field to intermediate element
    addDispatch({
      type: 'ADD_SUB',
      value: {
        superPartitionKey: partitionKey,
        subPartitionKey: partitionKey,
        superId: part.elementId,
        subId: part.id,
        elementType,
      },
    })
  }
}

export function buildEssentialInformationDispatches({
  originalValues,
  currentValues,
  partitionKey,
  personId,
  appName,
  imagesState, // needed to unset other primaries, if required
  forcePrimaryPerson,
  isCensus,
}) {
  // Collect dispatches with de-dupe (avoid backend errors from duplicate actions)
  const dispatches = []
  const dispatchSet = new Set()
  const pushUnique = (d) => {
    if (!d) return
    const key = JSON.stringify(d)
    if (dispatchSet.has(key)) return
    dispatchSet.add(key)
    dispatches.push(d)
  }
  if (!originalValues || !currentValues) {
    return { dispatches, normalized: currentValues, meta: { changed: false } }
  }

  const sanitizeFormState = (obj) => {
    // nameFields have a parts array with potentially undefined values
    if (obj?.nameFields && Array.isArray(obj.nameFields)) {
      obj.nameFields.forEach((nameField) => {
        nameField.parts = nameField.parts.filter(Boolean)
      })
    }

    return obj
  }
  const originalSanitized = sanitizeFormState({ ...originalValues })
  const currentSanitized = sanitizeFormState({ ...currentValues })

  // Enhanced validation that supports both editing and empty forms
  validateFormData({
    originalValues: originalSanitized,
    currentValues: currentSanitized,
    partitionKey,
    personId,
    strict: false, // Allow more lenient validation for editing scenarios
  })
  const { nameMap } = indexOriginal(originalSanitized)

  const recordId = currentValues.recordId ?? originalValues.recordId
  const record = recordId && recordId !== MAKE_NEW_RECORD ? getNestedOrFloatingElementById(imagesState, recordId) : null

  // Handle new person creation - if this is a new person, create the PERSON element
  if (currentValues.id !== originalValues.id) {
    // Create record if needed - either new record or existing record not found

    if (recordId === MAKE_NEW_RECORD || (recordId && !record)) {
      const recordDispatches = batchCreateRecord({
        imagePartitionKey: partitionKey,
        recordArk: recordId,
      })
      recordDispatches.forEach(pushUnique)
    }

    // Create the PERSON element
    pushUnique({
      type: 'CREATE_ELEMENT',
      value: {
        elementType: 'PERSON',
        partitionKey,
        id: personId,
        primary: !!currentValues.primaryPerson,
      },
    })

    // If recordId supplied, add the new person to the record, else the person will be floating.
    if (recordId) {
      pushUnique({
        type: 'ADD_SUB',
        value: {
          superPartitionKey: record?.partitionKey || partitionKey,
          subPartitionKey: partitionKey,
          superId: recordId,
          subId: personId,
          elementType: 'RECORD',
        },
      })
    }

    // Always set primary status (true or false) after person creation
    pushUnique({
      type: 'SET_PRIMARY',
      value: { partitionKey, id: personId, primary: !!currentValues.primaryPerson },
    })
  } else if (originalValues.primaryPerson !== currentValues.primaryPerson) {
    // User explicitly changed the toggle — dispatch regardless of collection.
    pushUnique({
      type: 'SET_PRIMARY',
      value: { partitionKey, id: personId, primary: !!currentValues.primaryPerson },
    })
    if (currentValues.primaryPerson && !isCensus) {
      // Unset any other primary in same record (skipped for all census records)
      if (record) {
        record.subElements
          ?.filter((el) => el.elementType === 'PERSON' && el.primary && el.id !== personId)
          .forEach((p) =>
            pushUnique({
              type: 'SET_PRIMARY',
              value: { partitionKey: p.partitionKey, id: p.id, primary: false },
            })
          )
      }
    }
  } else if (forcePrimaryPerson && currentValues.primaryPerson) {
    // Stale-data correction for census records whose template allows SET_PRIMARY.
    // forcePrimaryPerson is false for collections that disallow SET_PRIMARY (e.g. 1931 Canada
    // Census), so this branch is already excluded for those. For allowed census types the form
    // forces primaryPerson=true, so originalValues and currentValues can both be true even when
    // SLS has person.primary=false — this dispatch corrects that without user interaction.
    pushUnique({
      type: 'SET_PRIMARY',
      value: { partitionKey, id: personId, primary: true },
    })
  }

  // Names & parts
  currentSanitized.names.forEach((name) => {
    const originalName = nameMap[name.id]
    // Updated business rules for added/removed:
    // - If both added & removed are true => NO ACTION (user created then canceled before save)
    // - If added true & removed false => create
    // - If removed true & added false => remove (only if it existed originally)
    // - If both false => diff existing
    if (name.added && name.removed) {
      return // no dispatches
    }
    if (name.added && !name.removed) {
      // Only create and add the NAME element if it has meaningful parts
      if (nameHasMeaningfulParts(name)) {
        createNewName({ name, partitionKey, personId, appName, dispatches, addDispatch: pushUnique })
      }
      return
    }
    if (name.removed && !name.added && originalName) {
      pushUnique({
        type: 'REM_SUB',
        value: {
          superPartitionKey: partitionKey,
          subPartitionKey: partitionKey,
          superId: personId,
          subId: name.id,
          elementType: 'PERSON',
        },
      })
      return
    }
    // Existing name - possible type change
    if (originalName?.type?.key !== name.type?.key) {
      pushUnique({
        type: 'SET_NAME_TYPE',
        value: { id: name.id, partitionKey, nameType: name?.type?.key },
      })
    }
    // Handle active parts (additions, updates, no-ops)
    name.parts.forEach((partType) => {
      const part = name[partType]
      if (!part) return
      const origPart = originalName ? originalName[partType] : null

      // If part was added and then removed, do nothing (user added then changed mind before save)
      if (part.added && part.removed) {
        return // no action
      }

      // If part is newly added and not removed, create new name part
      if (part.added && !part.removed) {
        createNewNamePart({ name, part, partType, partitionKey, appName, dispatches, addDispatch: pushUnique })
      }

      // Check for changes in linkedPersonIds for existing parts
      // Handles the Link Names Panel functionality where users can link/unlink persons to/from shared fields
      if (origPart && part.linkedPersonIds && !part.removed) {
        const { addedPersons, removedPersons } = compareLinkedPersons(
          origPart.linkedPersonIds || [],
          part.linkedPersonIds || []
        )

        // Handle linked person changes
        if (addedPersons.length > 0 || removedPersons.length > 0) {
          const sharedElement = getSharedElement(imagesState, part.id)

          addedPersons.forEach((p) => {
            const person = getNestedOrFloatingElementById(imagesState, p.id)

            const linkDispatches = linkSharedFieldToPerson({
              person,
              sharedElement,
              partitionKey,
              images: imagesState,
            })

            linkDispatches.forEach(pushUnique)

            // Update the localized name for the person to trigger re-render in UI
            pushUnique({
              type: 'SET_PERSON_LOCALIZED_NAME',
              value: {
                personId: person.id,
                localizedName: '',
                partitionKey: person?.partitionKey,
              },
            })
          })

          // Handle removing persons from shared element
          removedPersons.forEach((p) => {
            const person = getNestedOrFloatingElementById(imagesState, p.id)

            const unlinkDispatches = unlinkSharedFieldFromPerson({
              person,
              sharedElement,
              partitionKey,
              images: imagesState,
            })

            unlinkDispatches.forEach(pushUnique)

            // Update the localized name for the person to trigger re-render in UI
            pushUnique({
              type: 'SET_PERSON_LOCALIZED_NAME',
              value: {
                personId: person.id,
                localizedName: '',
                partitionKey: person?.partitionKey,
              },
            })
          })
        }
      }

      // If part existed originally and value changed, update field value
      if (origPart && valueChanged(origPart?.value, part?.value) && !part?.removed && hasMeaningfulValue(part?.value)) {
        const existingFieldValueDispatch = part.dispatches?.find((d) => d.type === 'ADD_FV' && d.value.id === part.id)
        if (existingFieldValueDispatch) {
          part.dispatches.forEach(pushUnique)
        } else {
          const attr = buildAttribution(appName)
          const fv = createFieldValue(part.value || '', attr)
          const fieldIdToUse = part?.id || origPart?.id
          pushUnique({ type: 'ADD_FV', value: { partitionKey, id: fieldIdToUse, fieldValue: fv } })
        }
      }
    })

    // Handle removed parts separately - only generate REM_SUB for parts that existed originally
    if (name.removedParts) {
      name.removedParts.forEach((partType) => {
        const part = name[partType]
        const origPart = originalName ? originalName[partType] : null

        // Critical: Only generate REM_SUB if the part existed in the original data (not newly added)
        // This prevents backend errors from trying to remove elements that were never created
        if (part && origPart && part?.removed && !part?.added && origPart?.elementId) {
          // Determine element type for this part to know what type of sub-element we're removing
          const elementType = fieldToElementTypeMap[partType]
          // Check if there's another active part using the same element type that would be affected by removing the intermediate element
          const isThatElementTypeUsedByAnotherPart = name.parts.some((pt) => {
            const p = name[pt]
            return pt !== partType && p && !p.removed && fieldToElementTypeMap[pt] === elementType
          })

          if (isThatElementTypeUsedByAnotherPart) {
            // If there is another active part using the same element type, we should NOT remove the intermediate element,
            pushUnique({
              type: 'REM_SUB',
              value: {
                superPartitionKey: partitionKey,
                subPartitionKey: partitionKey,
                superId: origPart.elementId, // Use elementId of the intermediate element
                subId: origPart.id, // Use the original part ID
                elementType,
              },
            })
          } else {
            // If no other active part is using the same element type, we can safely remove the intermediate element with REM_SUB.
            pushUnique({
              type: 'REM_SUB',
              value: {
                superPartitionKey: partitionKey,
                subPartitionKey: partitionKey,
                superId: name.id,
                subId: origPart.elementId, // Use elementId of the intermediate element, not the field element ID
                elementType: 'NAME',
              },
            })
          }

          // Note: The field element removal is handled automatically by the backend
          // when the intermediate element is removed, so we don't need a separate REM_SUB for the field
        }
      })
    }
  })

  // Handle removedNames array - generate REM_SUB for names that were removed
  if (currentSanitized?.removedNames && Array.isArray(currentSanitized.removedNames)) {
    currentSanitized.removedNames.forEach((removedName) => {
      // Only generate REM_SUB if the name has an ID and is marked as removed
      if (removedName?.id && removedName?.removed) {
        pushUnique({
          type: 'REM_SUB',
          value: {
            superPartitionKey: partitionKey,
            subPartitionKey: partitionKey,
            superId: personId,
            subId: removedName.id,
            elementType: 'PERSON',
          },
        })
      }
    })
  }

  // Simple fields (SEX_CODE)
  const dispatchProxy = { push: (...ds) => ds.forEach((d) => pushUnique(d)) }
  buildPersonDetailFieldDispatches({
    original: originalSanitized,
    current: currentSanitized,
    partitionKey,
    appName,
    dispatches: dispatchProxy,
    imagesState,
  })

  // Ensure the record and person ids are set (if applicable)
  if (recordId && dispatches.length > 0) {
    pushUnique({
      type: 'SET_RECORD_PERSON_IDS',
      value: recordId,
    })
  }

  return { dispatches, normalized: currentSanitized }
}
